import {
  type APIMessage,
  type APISearchResults,
  buildSearchQuery,
  parseSearch,
  type SearchQuery,
  THREAD_CHANNEL_TYPES,
} from '@alternativamais/chat-core';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { mensagemDeErro } from '../../../componentes/Avisos';
import { Icone } from '../../../componentes/Icone';
import { useCliente, useEstado } from '../../../chat/contexto';
import { rotaDoCanal } from '../Canal';
import { BotaoIr, Carregando, MensagemResumida, Vazio } from '../componentes';
import { resolvedores } from './resolver';

export type EstadoBusca = {
  texto: string;
  pagina: number;
  ordem: 'recentes' | 'antigos' | 'relevancia';
  tipo: 'mensagens' | 'arquivos' | 'imagens' | 'links' | 'fixadas';
};

const POR_PAGINA = 25;

const TIPOS: Array<[EstadoBusca['tipo'], string, Partial<SearchQuery>]> = [
  ['mensagens', 'Mensagens', {}],
  ['arquivos', 'Arquivos', { has: ['file'] }],
  ['imagens', 'Imagens', { has: ['image'] }],
  ['links', 'Links', { has: ['link'] }],
  ['fixadas', 'Fixadas', { pinned: true }],
];

/**
 * Resultados da busca (C7), no painel da direita: no servidor (ou na
 * conversa, em DM), com ordem, tipo, os termos marcados e páginas.
 */
export function ResultadosBusca({
  canalId,
  busca,
  aoMudar,
  aoFechar,
}: {
  canalId: string;
  busca: EstadoBusca;
  aoMudar: (busca: EstadoBusca) => void;
  aoFechar: () => void;
}) {
  const cliente = useCliente();
  const navegar = useNavigate();
  const guildId = useEstado(
    (estado) => estado.channels[canalId]?.guild_id ?? null,
  );
  // Resposta da busca atual (a de uma busca anterior não aparece).
  const [resposta, setResposta] = useState<{
    para: unknown;
    resultado?: APISearchResults;
    erro?: string;
  } | null>(null);

  const montada = useMemo(() => {
    const analisada = parseSearch(busca.texto);
    const { query, invalid } = buildSearchQuery(
      analisada,
      resolvedores(cliente.store.getState(), guildId, canalId),
    );
    const extra = TIPOS.find(([tipo]) => tipo === busca.tipo)?.[2] ?? {};
    const final: SearchQuery = {
      ...query,
      ...(extra.pinned ? { pinned: true } : {}),
      ...(extra.has ? { has: [...(query.has ?? []), ...extra.has] } : {}),
      sort_by: busca.ordem === 'relevancia' ? 'relevance' : 'timestamp',
      sort_order: busca.ordem === 'antigos' ? 'asc' : 'desc',
      offset: (busca.pagina - 1) * POR_PAGINA,
      limit: POR_PAGINA,
    };
    const termos = analisada.content.split(/\s+/).filter(Boolean);
    return { query: final, invalidos: invalid, termos };
  }, [busca, cliente, guildId, canalId]);

  const resultado =
    resposta?.para === montada ? (resposta.resultado ?? null) : null;
  const erro = resposta?.para === montada ? (resposta.erro ?? null) : null;

  useEffect(() => {
    let vivo = true;
    const pedido = guildId
      ? cliente.api.searchGuild(guildId, montada.query)
      : cliente.api.searchChannel(canalId, montada.query);
    pedido
      .then((achados) => {
        if (!vivo) return;
        cliente.store
          .getState()
          .upsertUsers(
            achados.messages.flatMap((grupo) =>
              grupo.map((item) => item.author),
            ),
          );
        for (const thread of achados.threads)
          cliente.store.getState().upsertChannel(thread);
        setResposta({ para: montada, resultado: achados });
      })
      .catch(
        (falha) =>
          vivo && setResposta({ para: montada, erro: mensagemDeErro(falha) }),
      );
    return () => {
      vivo = false;
    };
  }, [cliente, guildId, canalId, montada]);

  const paginas = resultado
    ? Math.ceil(resultado.total_results / POR_PAGINA)
    : 0;
  const grupos = useMemo(() => agrupar(resultado), [resultado]);

  return (
    <aside
      aria-label="Resultados da busca"
      className="flex w-[440px] max-w-[50%] shrink-0 flex-col border-l border-divisor bg-lateral"
    >
      <header className="flex h-12 shrink-0 items-center gap-2 border-b border-divisor pr-2 pl-4">
        <h2 className="m-0 grow truncate text-[15px] font-semibold">
          {resultado
            ? `${resultado.total_results.toLocaleString('pt-BR')} ${
                resultado.total_results === 1 ? 'resultado' : 'resultados'
              }`
            : 'Buscando…'}
        </h2>
        <select
          aria-label="Ordenar resultados"
          value={busca.ordem}
          onChange={(evento) =>
            aoMudar({
              ...busca,
              pagina: 1,
              ordem: evento.target.value as EstadoBusca['ordem'],
            })
          }
          className="h-8 rounded-md border border-borda bg-chat px-2 text-sm text-texto"
        >
          <option value="recentes">Mais recentes</option>
          <option value="antigos">Mais antigos</option>
          <option value="relevancia">Relevância</option>
        </select>
        <button
          type="button"
          aria-label="Fechar busca"
          onClick={aoFechar}
          className="flex size-8 items-center justify-center rounded-md text-texto-3 hover:text-texto"
        >
          <Icone nome="x" tamanho={18} />
        </button>
      </header>
      <div
        role="tablist"
        aria-label="Tipo de resultado"
        className="flex shrink-0 gap-1 overflow-x-auto border-b border-divisor px-3 py-2"
      >
        {TIPOS.map(([tipo, rotulo]) => (
          <button
            key={tipo}
            type="button"
            role="tab"
            aria-selected={busca.tipo === tipo}
            onClick={() => aoMudar({ ...busca, pagina: 1, tipo })}
            className={`h-7 shrink-0 rounded-md px-2.5 text-sm font-semibold ${
              busca.tipo === tipo
                ? 'bg-ativo text-texto'
                : 'text-texto-2 hover:text-texto'
            }`}
          >
            {rotulo}
          </button>
        ))}
      </div>
      {montada.invalidos.length > 0 && (
        <p className="m-0 shrink-0 border-b border-divisor px-4 py-2 text-xs text-ocupado">
          Não entendi:{' '}
          {montada.invalidos
            .map((filtro) => `${filtro.key}:${filtro.value}`)
            .join(', ')}
        </p>
      )}
      <div className="rolagem min-h-0 grow overflow-y-auto p-3">
        {erro ? (
          <Vazio icone="alerta" titulo="A busca falhou" texto={erro} />
        ) : !resultado ? (
          <Carregando texto="Buscando…" />
        ) : !resultado.total_results ? (
          <Vazio
            icone="busca"
            titulo="Nada encontrado"
            texto="Tente outras palavras ou tire algum filtro."
          />
        ) : (
          <div className="flex flex-col gap-3">
            {grupos.map((grupo) => (
              <section key={grupo.chave} className="flex flex-col gap-2">
                <NomeDoCanal canalId={grupo.canalId} />
                {grupo.mensagens.map((mensagem) => (
                  <MensagemResumida
                    key={mensagem.id}
                    mensagem={mensagem}
                    guildId={guildId}
                    destacar={montada.termos}
                    data={new Date(mensagem.timestamp).toLocaleString('pt-BR', {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })}
                    acoes={
                      <BotaoIr
                        aoClicar={() =>
                          navegar(
                            `${rotaDoCanal({ id: mensagem.channel_id, guild_id: guildId })}/${mensagem.id}`,
                          )
                        }
                      />
                    }
                  />
                ))}
              </section>
            ))}
          </div>
        )}
      </div>
      {paginas > 1 && (
        <nav
          aria-label="Páginas de resultados"
          className="flex shrink-0 items-center justify-center gap-1 border-t border-divisor px-3 py-2"
        >
          <BotaoPagina
            rotulo="Página anterior"
            desabilitado={busca.pagina <= 1}
            aoClicar={() => aoMudar({ ...busca, pagina: busca.pagina - 1 })}
          >
            <Icone nome="chevron-esquerda" tamanho={16} />
          </BotaoPagina>
          {numerosDePagina(busca.pagina, paginas).map((numero, indice) =>
            numero === null ? (
              <span key={`r${indice}`} className="px-1 text-texto-3">
                …
              </span>
            ) : (
              <BotaoPagina
                key={numero}
                rotulo={`Página ${numero}`}
                atual={numero === busca.pagina}
                aoClicar={() => aoMudar({ ...busca, pagina: numero })}
              >
                {numero}
              </BotaoPagina>
            ),
          )}
          <BotaoPagina
            rotulo="Próxima página"
            desabilitado={busca.pagina >= paginas}
            aoClicar={() => aoMudar({ ...busca, pagina: busca.pagina + 1 })}
          >
            <Icone nome="chevron-direita" tamanho={16} />
          </BotaoPagina>
        </nav>
      )}
    </aside>
  );
}

function agrupar(resultado: APISearchResults | null) {
  const grupos: Array<{
    chave: string;
    canalId: string;
    mensagens: APIMessage[];
  }> = [];
  for (const [mensagem] of resultado?.messages ?? []) {
    if (!mensagem) continue;
    const ultimo = grupos[grupos.length - 1];
    if (ultimo && ultimo.canalId === mensagem.channel_id) {
      ultimo.mensagens.push(mensagem);
    } else {
      grupos.push({
        chave: `${mensagem.channel_id}-${mensagem.id}`,
        canalId: mensagem.channel_id,
        mensagens: [mensagem],
      });
    }
  }
  return grupos;
}

/** 1 … 4 5 [6] 7 8 … 20 */
function numerosDePagina(atual: number, total: number): Array<number | null> {
  const numeros = new Set([1, total, atual - 1, atual, atual + 1]);
  const lista = [...numeros]
    .filter((n) => n >= 1 && n <= total)
    .sort((a, b) => a - b);
  const resultado: Array<number | null> = [];
  for (const numero of lista) {
    const anterior = resultado[resultado.length - 1];
    if (typeof anterior === 'number' && numero - anterior > 1)
      resultado.push(null);
    resultado.push(numero);
  }
  return resultado;
}

function BotaoPagina({
  children,
  rotulo,
  aoClicar,
  atual = false,
  desabilitado = false,
}: {
  children: React.ReactNode;
  rotulo: string;
  aoClicar: () => void;
  atual?: boolean;
  desabilitado?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={rotulo}
      aria-current={atual ? 'page' : undefined}
      disabled={desabilitado}
      onClick={aoClicar}
      className={`flex h-8 min-w-8 items-center justify-center rounded-md px-2 text-sm font-semibold disabled:opacity-40 ${
        atual
          ? 'bg-destaque text-destaque-texto'
          : 'text-texto-2 hover:bg-ativo'
      }`}
    >
      {children}
    </button>
  );
}

function NomeDoCanal({ canalId }: { canalId: string }) {
  const canal = useEstado((estado) => estado.channels[canalId]);
  const thread = canal && THREAD_CHANNEL_TYPES.has(canal.type);
  return (
    <h3 className="m-0 flex items-center gap-1.5 px-1 text-sm font-semibold text-texto-2">
      <Icone
        nome={thread ? 'thread' : 'hash'}
        tamanho={16}
        className="text-texto-3"
      />
      <span className="truncate">{canal?.name ?? 'canal'}</span>
    </h3>
  );
}
